import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { HiChatAlt2, HiPaperAirplane } from "react-icons/hi";
import { agentChat } from "../../service/Agent";
import AppContext from "../../context/AppContext";
import ModalCloseButton from "./ModalCloseButton";

const baseURL = (process.env.REACT_APP_BASE_BACKEND_API_URL || "").replace(/\/$/, "");

/**
 * Resolve relative hrefs in agent HTML (e.g. /api/v1/agent/download/...) to full backend URL
 * so download links work when opened in the browser.
 */
const resolveMessageHrefs = (html) => {
  if (!html || !baseURL) return html;
  return html.replace(
    /href=(["'])\/([^"']*)\1/g,
    (_, quote, path) => `href=${quote}${baseURL}/${path}${quote}`
  );
};

const generateSessionId = () => {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/** Max input height (px) before scrolling; keeps the chat panel usable. */
const INPUT_TEXTAREA_MAX_HEIGHT = 200;

const AgentChatWidget = () => {
  const { langMode } = useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([]);
  const sessionIdRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputTextareaRef = useRef(null);

  const syncInputTextareaHeight = useCallback(() => {
    const el = inputTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, INPUT_TEXTAREA_MAX_HEIGHT);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > INPUT_TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
  }, []);

  useLayoutEffect(() => {
    syncInputTextareaHeight();
  }, [input, syncInputTextareaHeight]);

  useEffect(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = generateSessionId();
    }
    setMessages([
      {
        id: "welcome",
        sender: "agent",
        html: "<b>Hi!</b> I'm your Blufield AI assistant. Ask me about accounts, recovery targets, or try <i>find top 500 accounts</i>.",
        data: null,
        intentUsed: null,
      },
    ]);
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) return;

    setError("");
    const userMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      html: trimmed,
      data: null,
      intentUsed: null,
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    try {
      setIsSending(true);
      setIsAgentTyping(true);
      const sessionId = sessionIdRef.current;
      const response = await agentChat({ message: trimmed, sessionId });

      const agentMessage = {
        id: `agent-${Date.now()}`,
        sender: "agent",
        html: response.message || "I couldn't find an answer. Try rephrasing your question.",
        data: response.data || [],
        intentUsed: response.intentUsed || null,
      };

      setMessages((prev) => [...prev, agentMessage]);
    } catch (err) {
      console.error("Agent chat error:", err);
      const backendMessage =
        err?.response?.data?.body?.value ??
        err?.response?.data?.message ??
        err?.body?.value;
      if (backendMessage) {
        setError(backendMessage);
        setMessages((prev) => [
          ...prev,
          {
            id: `agent-error-${Date.now()}`,
            sender: "agent",
            html: backendMessage,
            data: null,
            intentUsed: null,
          },
        ]);
      }
    } finally {
      setIsSending(false);
      setIsAgentTyping(false);
    }
  };

  const renderAccountData = (data) => {
    if (!data || !data.length) return null;

    return (
      <div className="mt-3 rounded-lg overflow-hidden border border-gray-200/80 bg-white/90 shadow-sm">
        <div className="px-3 py-2 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-100 text-[11px] font-semibold text-slate-600 tracking-wide">
          Accounts ({data.length})
        </div>
        <div className="max-h-48 overflow-y-auto text-[11px] scrollbar-thin">
          <table className="min-w-full text-left">
            <thead className="bg-slate-50/80 sticky top-0">
              <tr>
                <th className="px-3 py-2 font-medium text-slate-600">Account</th>
                <th className="px-3 py-2 font-medium text-slate-600">Name</th>
                <th className="px-3 py-2 font-medium text-slate-600">Mobile</th>
                <th className="px-3 py-2 font-medium text-slate-600">Current OS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((account) => {
                const details = account.accountDetailsJson || {};
                const accountNo = details["Account No."] || "-";
                const name = details["CONSUMER_NAME_ENG"] || "Unknown";
                const mobile = details["Mobile No"] || "-";
                let currentOs = account.currentOs;

                if (typeof currentOs === "number") {
                  try {
                    currentOs = currentOs.toLocaleString("en-OM", {
                      style: "currency",
                      currency: "OMR",
                    });
                  } catch {
                    currentOs = currentOs.toString();
                  }
                } else if (currentOs == null) {
                  currentOs = "-";
                }

                return (
                  <tr key={account.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-3 py-2 text-slate-700 font-medium">{accountNo}</td>
                    <td className="px-3 py-2 text-slate-600">{name}</td>
                    <td className="px-3 py-2 text-slate-600">{mobile}</td>
                    <td className="px-3 py-2 text-slate-700">{currentOs}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className={`fixed bottom-6 z-50 font-sans ${isRtl ? "left-6" : "right-6"}`}>
      {/* Floating action button */}
      {!isOpen && (
        <button
          type="button"
          onClick={handleToggle}
          aria-label="Open AI chat"
          className="flex items-center justify-center w-14 h-14 rounded-lg shadow-lg bg-gradient-to-br from-[#236DB4] to-[#1a5490] text-white hover:from-[#2a7dd4] hover:to-[#236DB4] focus:outline-none focus:ring-4 focus:ring-[#236DB4]/40 transition-all duration-300 hover:scale-105 active:scale-95"
        >
          <HiChatAlt2 className="w-7 h-7" />
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className="w-[380px] h-[520px] flex flex-col rounded-lg shadow-lg border border-gray-200/60 bg-white overflow-hidden animate-in"
          style={{
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.04)",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3.5 shrink-0"
            style={{
              background: "linear-gradient(135deg, #236DB4 0%, #1a5490 100%)",
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <HiChatAlt2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-white tracking-tight">Blufield AI</h2>
                <p className="text-[11px] text-white/80">Accounts & recovery assistant</p>
              </div>
            </div>
            <ModalCloseButton
              onClick={handleToggle}
              ariaLabel="Close chat"
            />
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 bg-[#f8fafc] space-y-4 min-h-0">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[88%] rounded-lg px-4 py-2.5 text-[13px] leading-relaxed transform transition-all duration-200 ${
                    msg.sender === "user"
                      ? "bg-gradient-to-br from-[#236DB4] to-[#1a5490] text-white rounded-br-lg shadow-md origin-bottom-right"
                      : "bg-white text-slate-700 rounded-bl-lg shadow-sm border border-gray-100 origin-bottom-left"
                  }`}
                >
                  <div
                    className="agent-message prose prose-sm max-w-none prose-p:my-0.5 prose-b:text-inherit prose-i:text-inherit [&_a]:text-[#236DB4] [&_a]:underline [&_a]:font-medium [&_a:hover]:opacity-90"
                    dangerouslySetInnerHTML={{
                      __html: msg.sender === "agent" ? resolveMessageHrefs(msg.html) : msg.html,
                    }}
                  />
                  {msg.sender === "agent" && msg.data && msg.data.length > 0 && renderAccountData(msg.data)}
                  {msg.intentUsed && msg.sender === "agent" && (
                    <div className="mt-2 pt-2 border-top border-gray-100 text-[10px] text-slate-500 font-medium">
                      {msg.intentUsed}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Typing indicator while waiting for agent response */}
            {isAgentTyping && (
              <div className="flex justify-start">
                <div className="inline-flex items-center gap-1.5 rounded-lg rounded-bl-lg bg-white px-3 py-2 text-[11px] text-slate-500 shadow-sm border border-gray-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.2s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.1s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="p-4 pt-3 border-t border-gray-100 bg-white shrink-0">
            {error && (
              <p className="mb-2 text-[11px] text-rose-500 bg-rose-50 px-3 py-1.5 rounded-lg">
                {error}
              </p>
            )}
            <div className="flex items-end gap-2">
              <textarea
                ref={inputTextareaRef}
                rows={1}
                className="flex-1 min-h-[42px] max-h-[200px] resize-none overflow-hidden text-[13px] text-slate-800 placeholder-slate-400 border border-gray-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#236DB4]/30 focus:border-[#236DB4] transition-shadow"
                placeholder='e.g. "find top 500 accounts"'
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onInput={(e) => {
                  const el = e.target;
                  el.style.height = "auto";

                  const maxHeight = 80; // 2 rows
                  el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
                  el.style.lineHeight = "22px";
                  el.style.overflowY =
                    el.scrollHeight > maxHeight ? "auto" : "hidden";
                }}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={isSending || !input.trim()}
                className={`flex items-center justify-center w-10 h-10 rounded-lg shrink-0 transition-all ${
                  isSending || !input.trim()
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-br from-[#236DB4] to-[#1a5490] text-white hover:from-[#2a7dd4] hover:to-[#236DB4] shadow-md hover:shadow-lg active:scale-95"
                }`}
              >
                {isSending ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <HiPaperAirplane className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentChatWidget;

